import { useState, useEffect, useCallback, useRef } from 'react';
import { exportHistoryApi } from '@/api/api';
import type { ExportHistoryItem, GetExportHistoryParams } from '@/api/api';

export interface UseExportHistoryOptions {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UseExportHistoryReturn {
  data: ExportHistoryItem[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Export History Hook - 封装导出历史数据的获取和分页加载
 * 不实现缓存机制，确保每次获取的都是最新数据
 */
export const useExportHistory = (
  options: UseExportHistoryOptions = {}
): UseExportHistoryReturn => {
  const { page = 1, pageSize: page_size = 20, search } = options;

  const [data, setData] = useState<ExportHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(page);

  // 使用 ref 来跟踪是否正在加载，避免重复请求
  const isLoadingRef = useRef(false);
  // 使用 ref 来跟踪之前的搜索关键词，避免重复请求
  const prevSearchRef = useRef<string | undefined>(undefined);

  /**
   * 获取数据
   */
  const fetchData = useCallback(
    async (pageNum: number, append: boolean = false) => {
      // 防止重复请求
      if (isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const params: GetExportHistoryParams = {
          page: pageNum,
          pageSize: page_size,
          orderBy: '-createdAt', // 按创建时间倒序，最新的在前
        };

        if (search) {
          params.q = search;
        }

        const response = await exportHistoryApi.getExportHistory(params);
        
        // 处理响应数据
        // 后端返回格式: { results: [...], pagination: { page, pageSize, totalResults, totalPages }, summary: null }
        // 注意：axios 拦截器已经解包了数据，response 就是后端返回的数据对象，不是标准的 axios 响应对象
        let items: ExportHistoryItem[] = [];
        let total = 0;
        let pageSize = page_size;

        // axios 拦截器已经解包，response 就是后端返回的数据对象
        const responseData = response as any;

        if (responseData && responseData.results && Array.isArray(responseData.results)) {
          items = responseData.results;
          if (responseData.pagination) {
            total = responseData.pagination.totalResults || 0;
            pageSize = responseData.pagination.pageSize || page_size;
          }
        }

        if (append) {
          // 追加模式：将新数据追加到现有数据
          setData((prev) => [...prev, ...items]);
          setCurrentPage(pageNum); // 追加模式下也要更新当前页码
        } else {
          // 替换模式：替换所有数据
          setData(items);
          setCurrentPage(pageNum);
        }

        // 判断是否还有更多数据
        const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
        const hasMoreData = pageNum < totalPages && items.length === pageSize;
        setHasMore(hasMoreData);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch export history');
        setError(error);
        console.error('Error fetching export history:', error);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [page_size, search]
  );

  /**
   * 搜索关键词变化时重新加载
   * 注意：不自动加载初始数据，由组件在需要时调用 refetch()
   */
  useEffect(() => {
    // 只有当搜索关键词真正变化时才重新加载（避免初始加载）
    if (prevSearchRef.current !== undefined && prevSearchRef.current !== search) {
      void fetchData(1, false);
    }
    prevSearchRef.current = search;
  }, [search, fetchData]);

  /**
   * 加载更多数据
   */
  const loadMore = useCallback(async () => {
    if (loading || !hasMore || isLoadingRef.current) {
      return;
    }
    const nextPage = currentPage + 1;
    await fetchData(nextPage, true);
  }, [loading, hasMore, currentPage, fetchData]);

  /**
   * 手动刷新数据
   */
  const refetch = useCallback(async () => {
    await fetchData(1, false);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    refetch,
  };
};

